import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Tooltip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Download as DownloadIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useParams } from 'react-router-dom';
import calendarService, { type CalendarEvent, type AgencyUser } from '../services/calendarService';
import openScheduleService from '../services/openScheduleService';
import { getProperties } from '../services/propertyService';

interface Property {
  _id: string;
  primaryAssignee: string;
  secondaryAssignee?: string;
  tertiaryAssignee?: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
}


const Calendar: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const { user, agency } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [agencyUsers, setAgencyUsers] = useState<AgencyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day' | 'matrix'>('month');
  const [previousView, setPreviousView] = useState<'month' | 'week' | 'day'>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [navigationDialog, setNavigationDialog] = useState<{
    open: boolean;
    propertyId: string;
    propertyAddress: string;
  }>({ open: false, propertyId: '', propertyAddress: '' });
  
  // Admin view state
  const [calendarView, setCalendarView] = useState<'personal' | 'agency'>('personal');
  const [selectedUserId, setSelectedUserId] = useState<string>(user?.id || '');
  const isAdmin = user?.primaryRole === 'admin' || user?.primaryRole === 'app_admin';

  const navigate = useNavigate();

  const startOfWeekMonday = (date: Date): Date => {
    const d = new Date(date);
    const idx = (d.getDay() + 6) % 7; // Monday index
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - idx);
    return d;
  };

  const handleChangeView = (view: 'month' | 'week' | 'day' | 'matrix') => {
    if (view === 'matrix') {
      // Only allow matrix view for week and day, not month
      if (currentView === 'month') {
        return; // Don't switch to matrix from month view
      }
      // Store the current view as previous view before switching to matrix
      setPreviousView(currentView as 'month' | 'week' | 'day');
      setCurrentView(view);
      const today = new Date();
      setCurrentDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
    } else {
      // If currently in matrix view, keep matrix selected when switching between week/day
      if (currentView === 'matrix' && (view === 'week' || view === 'day')) {
        setPreviousView(view);
        // Stay in matrix view but update the underlying view
        const today = new Date();
        if (view === 'week') {
          setCurrentDate(startOfWeekMonday(today));
        } else if (view === 'day') {
          setCurrentDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
        }
        return;
      }
      
      // Normal view switching
      setCurrentView(view);
      const today = new Date();
      if (view === 'month') {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        setCurrentDate(d);
      } else if (view === 'week') {
        setCurrentDate(startOfWeekMonday(today));
      } else if (view === 'day') {
        setCurrentDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
      }
    }
  };

  const goToday = () => {
    handleChangeView(currentView); // resets to current period
  };

  const goPrev = () => {
    const d = new Date(currentDate);
    if (currentView === 'month') {
      d.setMonth(d.getMonth() - 1);
    } else if (currentView === 'week') {
      d.setDate(d.getDate() - 7);
    } else if (currentView === 'day' || currentView === 'matrix') {
      d.setDate(d.getDate() - 1);
    }
    setCurrentDate(d);
  };

  const goNext = () => {
    const d = new Date(currentDate);
    if (currentView === 'month') {
      d.setMonth(d.getMonth() + 1);
    } else if (currentView === 'week') {
      d.setDate(d.getDate() + 7);
    } else if (currentView === 'day' || currentView === 'matrix') {
      d.setDate(d.getDate() + 1);
    }
    setCurrentDate(d);
  };

  // Matrix view functions
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = new Date(currentDate);
        time.setHours(hour, minute, 0, 0);
        slots.push(time);
      }
    }
    return slots;
  };

  const getPropertyRowsWithAssignees = () => {
    const propertyRows: Array<{
      propertyId: string;
      property: any;
      assigneeNames: string;
      assigneeIds: string[];
    }> = [];
    
    // Get properties that have events on the current date
    // Apply the same filtering logic as loadData() to respect admin view settings
    let filteredEvents = events;
    
    if (isAdmin) {
      if (calendarView === 'agency') {
        // Show all events for the agency (no additional filtering needed)
        filteredEvents = events;
      } else if (selectedUserId) {
        // Show events for specific user - filter by both event creator AND property assignee
        filteredEvents = events.filter(event => {
          // Include events created by the selected user
          if (event.userId === selectedUserId) {
            return true;
          }
          
          // Include events for properties assigned to the selected user
          if (event.relatedProperty?._id) {
            const property = properties.find(p => p._id === event.relatedProperty!._id);
            if (property) {
              return property.primaryAssignee === selectedUserId ||
                     property.secondaryAssignee === selectedUserId ||
                     property.tertiaryAssignee === selectedUserId;
            }
          }
          
          return false;
        });
      } else {
        // Show admin's own events
        filteredEvents = events.filter(event => event.userId === user?.id);
      }
    } else {
      // Regular user can only see their own events
      filteredEvents = events.filter(event => event.userId === user?.id);
    }
    
    const currentDateEvents = filteredEvents.filter(event => {
      const eventDate = new Date(event.startTime);
      eventDate.setHours(0, 0, 0, 0);
      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      return eventDate.getTime() === currentDateOnly.getTime();
    });
    
    const propertyIds = new Set<string>();
    currentDateEvents.forEach(event => {
      if (event.relatedProperty?._id) {
        propertyIds.add(event.relatedProperty._id);
      }
    });
    
    // Create rows for each property with events
    propertyIds.forEach(propertyId => {
      const property = properties.find(p => p._id === propertyId);
      
      if (property) {
        const assigneeIdSet = new Set<string>();
        const assigneeNameList: string[] = [];

        // 1) Prefer assignees from events' teamMembers for the selected date
        const eventsForProperty = currentDateEvents.filter(e => e.relatedProperty?._id === propertyId);
        for (const ev of eventsForProperty) {
          const team = (ev as any).teamMembers as Array<{ userId?: string; _id?: string; name?: string; email?: string }> | undefined;
          if (Array.isArray(team)) {
            for (const m of team) {
              const uid = (m && (m.userId || (m as any)._id)) as string | undefined;
              if (uid) {
                if (!assigneeIdSet.has(uid)) {
                  assigneeIdSet.add(uid);
                  const user = agencyUsers.find(u => u._id === uid);
                  if (user) assigneeNameList.push(`${user.firstName} ${user.lastName}`);
                  else if (m.name) assigneeNameList.push(m.name);
                }
              } else {
                // No user id on team member, try email or name only
                const byEmail = m.email ? agencyUsers.find(u => (u as any).email === m.email) : undefined;
                if (byEmail) {
                  if (!assigneeIdSet.has(byEmail._id)) assigneeIdSet.add(byEmail._id);
                  assigneeNameList.push(`${byEmail.firstName} ${byEmail.lastName}`);
                } else if (m.name) {
                  // Name-only entry
                  assigneeNameList.push(m.name);
                }
              }
            }
          }
        }

        // 2) Fallback to property assignees if no team members found
        if (assigneeIdSet.size === 0) {
          const pushAssignee = (uid?: string) => {
            if (!uid) return;
            if (!assigneeIdSet.has(uid)) {
              assigneeIdSet.add(uid);
              const user = agencyUsers.find(u => u._id === uid);
              if (user) assigneeNameList.push(`${user.firstName} ${user.lastName}`);
            }
          };
          pushAssignee(property.primaryAssignee as any);
          pushAssignee(property.secondaryAssignee as any);
          pushAssignee(property.tertiaryAssignee as any);
        }

        // If still none, mark as unassigned
        const finalIds = Array.from(assigneeIdSet);
        const finalNames = assigneeNameList.length > 0 ? assigneeNameList.join(', ') : 'Unassigned';
        
        if (finalIds.length > 0 || finalNames === 'Unassigned') {
          propertyRows.push({
            propertyId,
            property,
            assigneeNames: finalNames,
            assigneeIds: finalIds
          });
        }
      }
    });
    return propertyRows;
  };

  const getPropertyAtTimeSlot = (propertyId: string, timeSlot: Date) => {
    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(currentDate);
    dayEnd.setHours(23, 59, 59, 999);

    // Apply the same filtering logic as loadData() to respect admin view settings
    let filteredEvents = events;
    
    if (isAdmin) {
      if (calendarView === 'agency') {
        // Show all events for the agency (no additional filtering needed)
        filteredEvents = events;
      } else if (selectedUserId) {
        // Show events for specific user - filter by both event creator AND property assignee
        filteredEvents = events.filter(event => {
          // Include events created by the selected user
          if (event.userId === selectedUserId) {
            return true;
          }
          
          // Include events for properties assigned to the selected user
          if (event.relatedProperty?._id) {
            const property = properties.find(p => p._id === event.relatedProperty!._id);
            if (property) {
              return property.primaryAssignee === selectedUserId ||
                     property.secondaryAssignee === selectedUserId ||
                     property.tertiaryAssignee === selectedUserId;
            }
          }
          
          return false;
        });
      } else {
        // Show admin's own events
        filteredEvents = events.filter(event => event.userId === user?.id);
      }
    } else {
      // Regular user can only see their own events
      filteredEvents = events.filter(event => event.userId === user?.id);
    }

    // Find events for this property at this time
    const relevantEvents = filteredEvents.filter(event => {
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      
      // Check if event overlaps with this time slot
      const slotStart = new Date(timeSlot);
      const slotEnd = new Date(timeSlot.getTime() + 30 * 60 * 1000); // 30 minutes
      
      // Check if event is on the current date
      const eventDate = new Date(eventStart);
      eventDate.setHours(0, 0, 0, 0);
      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      
      if (eventDate.getTime() !== currentDateOnly.getTime()) {
        return false;
      }
      
      // Check if event overlaps with time slot
      const timeOverlaps = eventStart < slotEnd && eventEnd > slotStart;
      
      if (!timeOverlaps) {
        return false;
      }
      
      // Check if event is for this property
      return event.relatedProperty?._id === propertyId;
    });

    if (relevantEvents.length > 0) {
      const firstEvent = relevantEvents[0];
      return {
        property: firstEvent.relatedProperty,
        events: relevantEvents
      };
    }

    return null;
  };

  // Get all events for one or more assignees that overlap a specific 30-minute slot
  const getAssigneeEventsAtTimeSlot = (assigneeIds: string[], timeSlot: Date) => {
    if (!assigneeIds || assigneeIds.length === 0) return [];

    const slotStart = new Date(timeSlot);
    const slotEnd = new Date(timeSlot.getTime() + 30 * 60 * 1000);

    return events.filter(event => {
      if (!event.startTime || !event.endTime) return false;

      // Only consider events belonging to these assignees
      if (!assigneeIds.includes(event.userId)) {
        return false;
      }

      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);

      // Same calendar day
      const eventDay = new Date(eventStart);
      eventDay.setHours(0, 0, 0, 0);
      const slotDay = new Date(slotStart);
      slotDay.setHours(0, 0, 0, 0);
      if (eventDay.getTime() !== slotDay.getTime()) {
        return false;
      }

      // Overlaps the slot
      return eventStart < slotEnd && eventEnd > slotStart;
    });
  };

  const exportMatrixToExcel = () => {
    const timeSlots = generateTimeSlots();
    const propertyRows = getPropertyRowsWithAssignees();
    
    // Build the complete data array with title, headers, and data
    const worksheetData: any[] = [];

    // Add company header row
    const companyName = agency?.name || 'Real Estate Agency';
    worksheetData.push([companyName, ...new Array(timeSlots.length).fill('')]);
    
    // Add title row
    const title = `📅 Schedule Matrix - ${currentDate.toLocaleDateString('en-AU', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`;
    worksheetData.push([title, ...new Array(timeSlots.length).fill('')]);
    
    // Add empty row for spacing
    worksheetData.push([...new Array(timeSlots.length + 1).fill('')]);
    
    // Add headers with icons
    const headers = ['👥 Team Members', ...timeSlots.map(slot => 
      `🕐 ${slot.toLocaleTimeString('en-AU', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })}`
    )];
    worksheetData.push(headers);

    // Add data rows with enhanced formatting
    propertyRows.forEach((propertyRow) => {
      const row = [`👤 ${propertyRow.assigneeNames}`];
      
      timeSlots.forEach(timeSlot => {
        const propertyData = getPropertyAtTimeSlot(propertyRow.propertyId, timeSlot);
        const assigneeEvents = getAssigneeEventsAtTimeSlot(propertyRow.assigneeIds, timeSlot);
        const primaryEvent = assigneeEvents[0] || (propertyData?.events && propertyData.events[0]);

        if (propertyData && propertyData.property && primaryEvent) {
          const address = propertyData.property.address as any;
          const addressStr = typeof address === 'string' ? address : 
            `${address.street}, ${address.city}`;
          
          // Add event type emoji and formatting – use the first overlapping event for labelling
          const eventType = primaryEvent.eventType;
          const eventEmoji = {
            'appointment': '📋',
            'meeting': '🤝',
            'open_house': '🏠',
            'inspection': '🔍',
            'follow_up': '📞',
            'other': '📝'
          }[eventType] || '📝';

          const conflictSuffix = assigneeEvents.length > 1 ? ` (+${assigneeEvents.length - 1} overlap)` : '';
          
          row.push(`${eventEmoji} ${addressStr}${conflictSuffix}`);
        } else {
          row.push('⚪ Available');
        }
      });
      
      worksheetData.push(row);
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths for better presentation
    const columnWidths = [
      { wch: 30 }, // Team Members column (wider for names and icons)
      ...timeSlots.map(() => ({ wch: 22 })) // Time slot columns (wider for property addresses and icons)
    ];
    worksheet['!cols'] = columnWidths;
    
    // Set row heights for better visual hierarchy
    const rowHeights = [
      { hpt: 25 }, // Company name row
      { hpt: 30 }, // Title row
      { hpt: 15 }, // Spacing row
      { hpt: 35 }, // Headers row
      ...propertyRows.map(() => ({ hpt: 28 })) // Data rows
    ];
    worksheet['!rows'] = rowHeights;
    
    // Enable styling support
    if (!worksheet['!rows']) worksheet['!rows'] = [];
    if (!worksheet['!cols']) worksheet['!cols'] = [];

    // Get the range for styling
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    
    // Define cosmic color palette
    const colors = {
      primary: '1E3A8A',      // Deep blue
      secondary: '3B82F6',    // Bright blue
      accent: '8B5CF6',       // Purple
      success: '10B981',      // Emerald
      warning: 'F59E0B',      // Amber
      danger: 'EF4444',       // Red
      light: 'F8FAFC',        // Light gray
      dark: '1F2937',         // Dark gray
      white: 'FFFFFF',        // White
      gradient1: 'E0E7FF',    // Light blue
      gradient2: 'DBEAFE',    // Lighter blue
      gradient3: 'BFDBFE',    // Even lighter blue
    };
    
    // Style company header row (row 0)
    if (worksheet['A1']) {
      worksheet['A1'].s = {
        font: { bold: true, size: 18, color: { rgb: colors.white } },
        fill: { fgColor: { rgb: colors.primary } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thick', color: { rgb: colors.primary } },
          bottom: { style: 'thick', color: { rgb: colors.primary } },
          left: { style: 'thick', color: { rgb: colors.primary } },
          right: { style: 'thick', color: { rgb: colors.primary } }
        }
      };
    }
    
    // Merge company header cells
    worksheet['!merges'] = worksheet['!merges'] || [];
    worksheet['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: timeSlots.length }
    });
    
    // Style title row (row 1)
    if (worksheet['A2']) {
      worksheet['A2'].s = {
        font: { bold: true, size: 16, color: { rgb: colors.primary } },
        fill: { fgColor: { rgb: colors.gradient1 } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'medium', color: { rgb: colors.secondary } },
          bottom: { style: 'medium', color: { rgb: colors.secondary } },
          left: { style: 'medium', color: { rgb: colors.secondary } },
          right: { style: 'medium', color: { rgb: colors.secondary } }
        }
      };
    }
    
    // Merge title cells
    worksheet['!merges'].push({
      s: { r: 1, c: 0 },
      e: { r: 1, c: timeSlots.length }
    });
    
    // Style header row (row 3)
    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 3, c: col });
      if (!worksheet[cellAddress]) continue;
      
      worksheet[cellAddress].s = {
        font: { bold: true, size: 12, color: { rgb: colors.white } },
        fill: { fgColor: { rgb: colors.secondary } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thick', color: { rgb: colors.primary } },
          bottom: { style: 'thick', color: { rgb: colors.primary } },
          left: { style: 'medium', color: { rgb: colors.primary } },
          right: { style: 'medium', color: { rgb: colors.primary } }
        }
      };
    }

    // Style data rows with cosmic formatting (starting from row 4)
    for (let row = 4; row <= range.e.r; row++) {
      const isEvenRow = (row - 4) % 2 === 0;
      const backgroundColor = isEvenRow ? colors.gradient2 : colors.gradient3;
      
      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) continue;
        
        // Style for team members column (first column)
        if (col === 0) {
          worksheet[cellAddress].s = {
            font: { bold: true, size: 11, color: { rgb: colors.dark } },
            fill: { fgColor: { rgb: backgroundColor } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: colors.secondary } },
              bottom: { style: 'thin', color: { rgb: colors.secondary } },
              left: { style: 'medium', color: { rgb: colors.primary } },
              right: { style: 'thin', color: { rgb: colors.secondary } }
            }
          };
        } else {
          // Style for time slot cells with event type color coding
          const cellValue = worksheet[cellAddress].v;
          const hasProperty = cellValue && cellValue !== '' && cellValue !== '⚪ Available';
          
          // Determine event type color
          let eventColor = colors.light;
          let textColor = colors.dark;
          let fontWeight = 'normal';
          
          if (hasProperty) {
            if (cellValue.includes('🏠')) { // Open house
              eventColor = colors.success;
              textColor = colors.white;
              fontWeight = 'bold';
            } else if (cellValue.includes('📋')) { // Appointment
              eventColor = colors.warning;
              textColor = colors.white;
              fontWeight = 'bold';
            } else if (cellValue.includes('🤝')) { // Meeting
              eventColor = colors.accent;
              textColor = colors.white;
              fontWeight = 'bold';
            } else if (cellValue.includes('🔍')) { // Inspection
              eventColor = colors.danger;
              textColor = colors.white;
              fontWeight = 'bold';
            } else {
              eventColor = colors.gradient1;
              textColor = colors.dark;
              fontWeight = 'bold';
            }
          } else {
            eventColor = backgroundColor;
            textColor = colors.dark;
            fontWeight = 'normal';
          }
          
          worksheet[cellAddress].s = {
            font: { 
              bold: fontWeight === 'bold', 
              size: 10, 
              color: { rgb: textColor } 
            },
            fill: { fgColor: { rgb: eventColor } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: colors.secondary } },
              bottom: { style: 'thin', color: { rgb: colors.secondary } },
              left: { style: 'thin', color: { rgb: colors.secondary } },
              right: { style: 'medium', color: { rgb: colors.primary } }
            }
          };
        }
      }
    }

    // Add summary section at the bottom
    const summaryRow = range.e.r + 2;
    const summaryData = [
      '📊 Summary',
      `Total Properties: ${propertyRows.length}`,
      `Time Slots: ${timeSlots.length}`,
      `Generated: ${new Date().toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    ];
    
    // Add summary data
    for (let i = 0; i < summaryData.length; i++) {
      const cellAddress = XLSX.utils.encode_cell({ r: summaryRow, c: i });
      worksheet[cellAddress] = { v: summaryData[i] };
      worksheet[cellAddress].s = {
        font: { bold: true, size: 10, color: { rgb: colors.primary } },
        fill: { fgColor: { rgb: colors.light } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: {
          top: { style: 'thick', color: { rgb: colors.primary } },
          bottom: { style: 'thin', color: { rgb: colors.secondary } },
          left: { style: 'thin', color: { rgb: colors.secondary } },
          right: { style: 'thin', color: { rgb: colors.secondary } }
        }
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, '📅 Schedule Matrix');
    
    // Generate filename with current date
    const filename = `📅 Schedule-Matrix-${currentDate.toISOString().split('T')[0]}.xlsx`;
    
    // Save file
    XLSX.writeFile(workbook, filename);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const switchToMatrixView = (date: Date) => {
    // Store the current view as previous view before switching to matrix
    setPreviousView(currentView as 'month' | 'week' | 'day');
    setCurrentDate(date);
    setCurrentView('matrix');
  };

  const handlePropertyClick = (property: any) => {
    if (!property || !property._id) return;
    
    const address = property.address as any;
    const addressStr = typeof address === 'string' ? address : 
      `${address.street}, ${address.city}`;
    
    setNavigationDialog({
      open: true,
      propertyId: property._id,
      propertyAddress: addressStr
    });
  };

  const handleNavigateToProperty = () => {
    if (navigationDialog.propertyId && agencySlug) {
      navigate(`/agencies/${agencySlug}/properties/${navigationDialog.propertyId}`);
    }
    setNavigationDialog({ open: false, propertyId: '', propertyAddress: '' });
  };

  const handleCancelNavigation = () => {
    setNavigationDialog({ open: false, propertyId: '', propertyAddress: '' });
  };
  


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build event filters based on admin view settings
      const eventFilters: any = {};
      
      if (isAdmin) {
        if (calendarView === 'agency') {
          // Show all events for the agency
          eventFilters.view = 'agency';
        } else if (selectedUserId) {
          eventFilters.userId = selectedUserId;
        }
      }

      const [eventsData, propertiesData] = await Promise.all([
        calendarService.getEvents(agencySlug!, eventFilters),
        getProperties(agencySlug!, 1, 100, false) // Load active properties (archivedOnly=false means active)
      ]);
      
      // Handle different response structures
      const propertiesList = Array.isArray(propertiesData) ? propertiesData : 
                            (propertiesData?.properties ? propertiesData.properties : []);
      
      // Also load upcoming property open sessions and map them into calendar events
      try {
        const formatPropertyAddress = (p: any): string => {
          if (!p) return '';
          // New schema shape: address is an object { street, city, state, country, postalCode }
          if (p.address && typeof p.address === 'object') {
            const parts = [p.address.street, p.address.city, p.address.state, p.address.postalCode]
              .filter(Boolean);
            return parts.join(', ');
          }
          // Legacy flat fields
          const parts = [p.address, p.suburb || p.city, p.state, p.postcode || p.postalCode].filter(Boolean);
          return parts.join(', ');
        };
        const upcoming = await openScheduleService.getUpcomingSessions(agencySlug!, 1, 200);
        
        // Filter out sessions from archived properties
        const activeProperties = propertiesList.filter(p => p.status !== 'archived');
        const activePropertyIds = new Set(activeProperties.map(p => p._id));
        
        const activeSessions = (upcoming?.data || []).filter((s: any) => {
          const hasProperty = s.property && s.property._id;
          const isActive = hasProperty && activePropertyIds.has(s.property._id);
          return isActive;
        });
        
        const mappedOpenSessions: CalendarEvent[] = activeSessions.map((s: any) => ({
          _id: s.sessionId || s._id,
          userId: user?.id || '', // Use current user as the creator of open house events
          title: s.property ? `${formatPropertyAddress(s.property)} - Open` : 'Property Open',
          description: s.notes || undefined,
          startTime: (() => {
            const start = new Date(s.date);
            const [h, m] = (s.startTime || '00:00').split(':').map((v: string) => parseInt(v, 10));
            if (Number.isFinite(h) && Number.isFinite(m)) {
              start.setHours(h, m, 0, 0);
            }
            return start.toISOString();
          })(),
          endTime: (() => {
            const start = new Date(s.date);
            const [h, m] = (s.endTime || s.startTime || '00:30').split(':').map((v: string) => parseInt(v, 10));
            const end = new Date(start);
            if (Number.isFinite(h) && Number.isFinite(m)) {
              end.setHours(h, m, 0, 0);
              // If end time is same as start time or earlier, add 30 minutes
              if (end <= start && s.startTime) {
                const [sh, sm] = s.startTime.split(':').map((v: string) => parseInt(v, 10));
                const tentative = new Date(start);
                tentative.setHours(sh ?? 0, sm ?? 0, 0, 0);
                tentative.setMinutes(tentative.getMinutes() + 30);
                return tentative.toISOString();
              }
              return end.toISOString();
            }
            const tentative = new Date(start);
            tentative.setMinutes(tentative.getMinutes() + 30);
            return tentative.toISOString();
          })(),
          allDay: false,
          location: s.property ? formatPropertyAddress(s.property) : undefined,
          eventType: 'open_house',
          status: 'scheduled',
          priority: 'medium',
          attendees: Array.isArray(s.attendees)
            ? s.attendees.map((a: any) => ({
                name: a.name,
                email: a.email,
                phone: a.phone,
                role: 'attendee',
                responseStatus: 'accepted'
              }))
            : [],
          relatedProperty: s.property ? {
            _id: s.property._id || '',
            address: formatPropertyAddress(s.property),
            suburb: s.property.suburb || s.property.address?.city || '',
            state: s.property.state || s.property.address?.state || '',
            postcode: s.property.postcode || s.property.address?.postalCode || ''
          } : undefined,
          isRecurring: false,
          notifications: [],
          reminderEnabled: false,
          reminderMinutes: 15,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: '',
          // carry through team members from backend for tooltip display
          ...(s.teamMembers ? { teamMembers: s.teamMembers } : {})
        }));
        setEvents([...eventsData, ...mappedOpenSessions]);
      } catch (e) {
        // Fallback to just personal/agency events if open sessions fail to load
      setEvents(eventsData);
      }
      
      setProperties(propertiesList);

      // Load agency users for assignee display
        try {
          const usersData = await calendarService.getAgencyUsers(agencySlug!);
          setAgencyUsers(usersData);
        } catch (err) {
          console.error('Failed to load agency users:', err);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [agencySlug, isAdmin, calendarView, selectedUserId]);

  useEffect(() => {
    if (agencySlug) {
      loadData();
    }
  }, [agencySlug, calendarView, selectedUserId, loadData]);

  // Set current user as selected when user data loads
  useEffect(() => {
    if (user?.id && selectedUserId !== user.id) {
      setSelectedUserId(user.id);
      setCalendarView('personal');
    }
  }, [user, selectedUserId]);

  const getEventsForDate = (date: Date) => {
    // Apply the same filtering logic as loadData() to respect admin view settings
    let filteredEvents = events;
    
    if (isAdmin) {
      if (calendarView === 'agency') {
        // Show all events for the agency (no additional filtering needed)
        filteredEvents = events;
      } else if (selectedUserId) {
        // Show events for specific user - filter by both event creator AND property assignee
        filteredEvents = events.filter(event => {
          // Include events created by the selected user
          if (event.userId === selectedUserId) {
            return true;
          }
          
          // Include events for properties assigned to the selected user
          if (event.relatedProperty?._id) {
            const property = properties.find(p => p._id === event.relatedProperty!._id);
            if (property) {
              return property.primaryAssignee === selectedUserId ||
                     property.secondaryAssignee === selectedUserId ||
                     property.tertiaryAssignee === selectedUserId;
            }
          }
          
          return false;
        });
      } else {
        // Show admin's own events
        filteredEvents = events.filter(event => event.userId === user?.id);
      }
    } else {
      // Regular user can only see their own events
      filteredEvents = events.filter(event => event.userId === user?.id);
    }
    
    return filteredEvents.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'appointment': return '#1976d2';
      case 'meeting': return '#388e3c';
      case 'open_house': return '#f57c00';
      case 'inspection': return '#7b1fa2';
      case 'follow_up': return '#d32f2f';
      default: return '#757575';
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Calendar
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* User Selection Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h6" sx={{ mr: 2 }}>
              Calendar View:
            </Typography>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>View User Calendar</InputLabel>
              <Select
                value={calendarView === 'agency' ? '' : selectedUserId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedUserId(value);
                  setCalendarView(value === '' ? 'agency' : 'personal');
                  loadData();
                }}
                label="View User Calendar"
              >
                <MenuItem value="">All Users</MenuItem>
                {agencyUsers.map(agencyUser => (
                  <MenuItem key={agencyUser._id} value={agencyUser._id}>
                    {agencyUser.firstName} {agencyUser.lastName} ({agencyUser.primaryRole})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendar Display Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant={currentView === 'month' ? 'contained' : 'outlined'}
              onClick={() => handleChangeView('month')}
            >
              Month
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={currentView === 'week' ? 'contained' : 'outlined'}
              onClick={() => handleChangeView('week')}
            >
              Week
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={currentView === 'day' ? 'contained' : 'outlined'}
              onClick={() => handleChangeView('day')}
            >
              Day
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={currentView === 'matrix' ? 'contained' : 'outlined'}
              onClick={() => handleChangeView('matrix')}
              disabled={currentView === 'month'}
            >
              Matrix {currentView === 'matrix' && `(${previousView})`}
            </Button>
          </Grid>
          <Grid item xs />
          <Grid item>
            <Button variant="outlined" onClick={goPrev} sx={{ mr: 1 }}>Prev</Button>
            <Button variant="outlined" onClick={goToday} sx={{ mr: 1 }}>Today</Button>
            <Button variant="outlined" onClick={goNext} sx={{ mr: 1 }}>Next</Button>
            {currentView === 'matrix' && (
              <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
                <MoreVertIcon />
              </IconButton>
            )}
          </Grid>
          <Grid item>
            <Typography variant="h6">
              {(() => {
                if (currentView === 'week') {
                  const firstOfWeek = (() => {
                    return startOfWeekMonday(currentDate);
                  })();
                  const lastOfWeek = (() => {
                    const d = new Date(firstOfWeek);
                    d.setDate(d.getDate() + 6);
                    return d;
                  })();
                  const startStr = firstOfWeek.toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short'
                  });
                  const endStr = lastOfWeek.toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  });
                  return `${startStr} – ${endStr}`;
                }
                if (currentView === 'day') {
                  return currentDate.toLocaleDateString('en-AU', {
                    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                  });
                }
                return currentDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
              })()}
              {isAdmin && calendarView === 'agency' && ' - Agency View'}
              {isAdmin && selectedUserId && ' - User View'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        {currentView === 'matrix' ? (
          // Matrix View
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Schedule Matrix - {currentDate.toLocaleDateString('en-AU', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentView(previousView)}
                sx={{ ml: 2 }}
              >
                Back to {previousView === 'month' ? 'Month' : previousView === 'week' ? 'Week' : 'Day'} View
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Assignee</TableCell>
                    {generateTimeSlots().map((timeSlot, index) => (
                      <TableCell key={index} align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                        {timeSlot.toLocaleTimeString('en-AU', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getPropertyRowsWithAssignees().map((propertyRow) => (
                    <TableRow key={propertyRow.propertyId}>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {propertyRow.assigneeNames}
                      </TableCell>
                      {generateTimeSlots().map((timeSlot, index) => {
                        const propertyData = getPropertyAtTimeSlot(propertyRow.propertyId, timeSlot);
                        const assigneeEvents = getAssigneeEventsAtTimeSlot(propertyRow.assigneeIds, timeSlot);

                        // Prefer an event that actually belongs to this property for display
                        const primaryEvent =
                          assigneeEvents.find(e => (e.relatedProperty as any)?._id === propertyRow.propertyId) ||
                          assigneeEvents[0] ||
                          (propertyData && propertyData.events && propertyData.events[0]) ||
                          null;

                        const hasConflict = assigneeEvents.length > 1;

                        return (
                          <TableCell
                            key={index}
                            align="center"
                            sx={{ 
                              backgroundColor: hasConflict ? '#ffebee' : (propertyData ? '#e3f2fd' : 'white'),
                              border: '1px solid #e0e0e0',
                              minHeight: 40
                            }}
                          >
                            {propertyData && propertyData.property && primaryEvent ? (
                              <Tooltip
                                title={`${primaryEvent.title} - ${new Date(primaryEvent.startTime).toLocaleTimeString('en-AU')} - ${new Date(primaryEvent.endTime).toLocaleTimeString('en-AU')}${hasConflict ? ` (+${assigneeEvents.length - 1} overlapping event${assigneeEvents.length - 1 > 1 ? 's' : ''})` : ''}`}
                              >
                                <Box 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: hasConflict ? '#c62828' : '#1976d2',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '4px',
                                      padding: '2px'
                                    }
                                  }}
                                  onClick={() => handlePropertyClick(propertyData.property)}
                                > 
                                  {(() => {
                                    const address = propertyData.property.address as any;
                                    if (typeof address === 'string') {
                                      return address.split(',')[0]; // Just street name
                                    }
                                    return address.street;
                                  })()}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box sx={{ color: '#ccc', fontSize: '0.75rem' }}>-</Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          // Regular Calendar Views
          (() => {
          // Headers based on view
          const renderHeaders = () => {
            if (currentView === 'day') {
              return (
                <Grid container spacing={1} columns={1}>
                  <Grid item xs={1}>
                    <Typography variant="subtitle2" align="center" sx={{ fontWeight: 'bold', p: 1 }}>
                      {currentDate.toLocaleDateString('en-AU', { weekday: 'short' })}
                    </Typography>
                  </Grid>
                </Grid>
              );
            }
            return (
              <Grid container spacing={1} columns={7}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <Grid item xs={1} key={day}>
              <Typography variant="subtitle2" align="center" sx={{ fontWeight: 'bold', p: 1 }}>
                {day}
              </Typography>
            </Grid>
          ))}
              </Grid>
            );
          };

          // Dates to render based on view
          const dates: Date[] = [];
          if (currentView === 'month') {
            const firstOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const mondayIndex = (firstOfMonth.getDay() + 6) % 7;
            const gridStart = new Date(firstOfMonth);
            gridStart.setDate(firstOfMonth.getDate() - mondayIndex);
            for (let i = 0; i < 42; i++) {
              const d = new Date(gridStart);
              d.setDate(gridStart.getDate() + i);
              dates.push(d);
            }
          } else if (currentView === 'week') {
            const start = startOfWeekMonday(currentDate);
            for (let i = 0; i < 7; i++) {
              const d = new Date(start);
              d.setDate(start.getDate() + i);
              dates.push(d);
            }
          } else {
            dates.push(new Date(currentDate));
          }

          const columns = currentView === 'day' ? 1 : 7;

          return (
            <>
              {renderHeaders()}
              <Grid container spacing={1} columns={columns}>
                {dates.map((date, i) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = date.toDateString() === new Date().toDateString();
            return (
                    <Grid item xs={1} key={i}>
                <Box
                  sx={{
                    minHeight: 120,
                    p: 1,
                    border: '1px solid #e0e0e0',
                    backgroundColor: isToday ? '#f3f3f3' : 'white',
                          opacity: currentView === 'month' ? (isCurrentMonth ? 1 : 0.5) : 1,
                    cursor: 'pointer',
                          position: 'relative',
                    '&:hover': {
                            backgroundColor: '#f5f5f5',
                            '& .matrix-button': {
                              opacity: 1,
                              visibility: 'visible'
                            }
                          }
                        }}
                        onClick={() => { /* Calendar is read-only */ }}
                        onMouseEnter={() => setHoveredDate(date)}
                        onMouseLeave={() => setHoveredDate(null)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: isToday ? 'bold' : 'normal' }}>
                    {date.getDate()}
                  </Typography>
                          {hoveredDate && hoveredDate.toDateString() === date.toDateString() && (
                            <Button
                              className="matrix-button"
                              size="small"
                              variant="contained"
                              sx={{
                                opacity: 0,
                                visibility: 'hidden',
                                transition: 'all 0.2s ease',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.5,
                                fontSize: '0.7rem',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                  backgroundColor: '#1565c0'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                switchToMatrixView(date);
                              }}
                            >
                              Matrix
                            </Button>
                          )}
                        </Box>
                        {dayEvents.map((event) => {
                          const hasAttendees = Array.isArray((event as any).teamMembers) && (event as any).teamMembers.length > 0;
                          const tooltipTitle = (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {event.title}
                              </Typography>
                              {hasAttendees && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Team</Typography>
                                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    {(event as any).teamMembers.slice(0, 5).map((a: any, i: number) => (
                                      <li key={`${event._id}-att-${i}`}>
                                        <Typography variant="caption">{a.name}{a.email ? ` (${a.email})` : ''}</Typography>
                                      </li>
                                    ))}
                                    {(event as any).teamMembers.length > 5 && (
                                      <li><Typography variant="caption">and more...</Typography></li>
                                    )}
                                  </Box>
                                </Box>
                              )}
                              {!hasAttendees && event.relatedCustomer && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption">Customer: {event.relatedCustomer.firstName} {event.relatedCustomer.lastName}</Typography>
                                </Box>
                              )}
                              {event.relatedProperty && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption">Property: {event.relatedProperty.address}</Typography>
                                </Box>
                              )}
                            </Box>
                          );
                          return (
                            <Tooltip key={event._id} title={tooltipTitle} arrow enterDelay={200}>
                              <Box
                      sx={{
                        backgroundColor: getEventTypeColor(event.eventType),
                        color: 'white',
                        p: 0.5,
                        mb: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                                  '&:hover': { opacity: 0.9 }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                                  if (event.relatedProperty) {
                                    handlePropertyClick(event.relatedProperty);
                                  }
                      }}
                    >
                                <Typography variant="caption" noWrap>{event.title}</Typography>
                    </Box>
                            </Tooltip>
                          );
                        })}
                </Box>
              </Grid>
            );
          })}
        </Grid>
            </>
          );
        })()
        )}
      </Paper>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={exportMatrixToExcel}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
                    </MenuItem>
      </Menu>

      {/* Navigation Confirmation Dialog */}
      <Dialog open={navigationDialog.open} onClose={handleCancelNavigation}>
        <DialogTitle>Navigate to Property</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to navigate to the property page for:
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, mb: 2, fontWeight: 'bold' }}>
            {navigationDialog.propertyAddress}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will take you to the property details page where you can view and manage the property's schedule.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>Cancel</Button>
          <Button onClick={handleNavigateToProperty} variant="contained">
            Go to Property
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Calendar; 